/**
 * "Forgot password?" against Better Auth, on the console's own origin.
 *
 * Two relative POSTs through `worker/auth-proxy.ts`, like sign-in. The first
 * asks for an email; Better Auth mails a link whose token check runs on the
 * auth Worker's base URL and then redirects to `redirectTo` — this console's
 * `/reset-password?token=…` (or `?error=INVALID_TOKEN`). The second sets the
 * new password with that token. The console host is in the auth Worker's
 * trusted origins, which is what lets it be the redirect target.
 */
export const REQUEST_RESET_PATH = "/api/auth/request-password-reset";
export const RESET_PASSWORD_PATH = "/api/auth/reset-password";

/** Better Auth's default minimum; checked here only to save a round trip. */
const MIN_PASSWORD_LENGTH = 8;

const INVALID_LINK =
  "This reset link is invalid or has expired. Request a new one.";

export type ResetOutcome = { ok: true } | { ok: false; message: string };

async function postJson(
  fetchFn: typeof fetch,
  path: string,
  body: Record<string, string>,
): Promise<ResetOutcome> {
  let response: Response;
  try {
    response = await fetchFn(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
  } catch {
    return {
      ok: false,
      message:
        "Could not reach the sign-in service. Check your connection and try again.",
    };
  }
  if (response.ok) return { ok: true };

  const parsed = (await response.json().catch(() => null)) as {
    message?: unknown;
  } | null;
  return {
    ok: false,
    message:
      typeof parsed?.message === "string" && parsed.message.length > 0
        ? parsed.message
        : "Something went wrong. Try again.",
  };
}

/**
 * Better Auth answers the same success whether or not the address has an
 * account, so the screen must not read `ok` as "that email exists".
 */
export function requestPasswordReset(
  { email }: { email: string },
  deps: { fetchFn?: typeof fetch; origin?: string } = {},
): Promise<ResetOutcome> {
  const trimmed = email.trim();
  if (!trimmed) {
    return Promise.resolve({ ok: false, message: "Enter your email." });
  }
  const origin = deps.origin ?? window.location.origin;
  return postJson(deps.fetchFn ?? fetch, REQUEST_RESET_PATH, {
    email: trimmed,
    redirectTo: `${origin}/reset-password`,
  });
}

export function resetPassword(
  input: { token: string; newPassword: string; confirm: string },
  deps: { fetchFn?: typeof fetch } = {},
): Promise<ResetOutcome> {
  if (!input.token)
    return Promise.resolve({ ok: false, message: INVALID_LINK });
  if (input.newPassword.length < MIN_PASSWORD_LENGTH) {
    return Promise.resolve({
      ok: false,
      message: `Use at least ${MIN_PASSWORD_LENGTH} characters.`,
    });
  }
  if (input.newPassword !== input.confirm) {
    return Promise.resolve({
      ok: false,
      message: "The two passwords do not match.",
    });
  }
  return postJson(deps.fetchFn ?? fetch, RESET_PASSWORD_PATH, {
    token: input.token,
    newPassword: input.newPassword,
  });
}

/** Why the landing URL cannot reset anything, or `null` when it can. */
export function resetLinkError(search: string): string | null {
  const params = new URLSearchParams(search);
  return params.get("error") || !params.get("token") ? INVALID_LINK : null;
}
